import { CSSResultGroup, TemplateResult, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import componentStyles from './tufts-social-links.css.lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { classMap } from 'lit/directives/class-map.js';
import type { HorizontalAlignment } from '../../base/outline-element/utils/types';
import '../tufts-icon/tufts-icon';
import { SlotNGController } from '../../controllers/slot-ng-controller';

/**
 * The TuftsSocialLinks component
 * @element tufts-social-links
 *
 * @slot text
 *
 * @attr facebook-account
 * @attr twitter-account
 * @attr linkedin-account
 * @attr youtube-account
 * @attr pintrest-account
 * @attr podcasts-account
 * @attr instagram-account
 * @attr web-name
 * @attr share-page-url
 * @attr screen-reader-text
 * @attr header-text
 * @attr link-size
 * @attr has-separator
 * @attr white-icons
 * @attr increased-gap
 * @attr target-aria-label
 * @attr mail-to
 * @attr content-align
 *
 * @property If you want the option to share a page on Facebook Twitter and LinkedIn add the URL of the page to share using the attribute 'share-page-url
 * @property Add a URL linking to Facebook with the attribute "fb-link"
 * @property Add a URL linking to Twitter with the attribute "tw-link"
 * @property Add a URL linking to LinkedIn with the attribute "li-link"
 * @property Add a URL linking to InstaGram with the attribute "ig-link"
 * @property Add a URL linking to YouTube with the attribute "yt-link"
 * @property Add a URL linking to Pinterest with the attribute "pst-link"
 * @property Add a URL linking to a Podcast with the attribute "pod-link"
 * @property Add a URL linking to a website with the attribute "web-link"
 */

@customElement('tufts-social-links')
export class TuftsSocialLinks extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];
  ngSlot = new SlotNGController(this);

  @property({ type: Boolean, attribute: 'not-track-icon-footer' })
  footerNoTracker: false;

  @property({ type: String, attribute: 'fb-link' })
  fbLink: string;

  @property({ type: String, attribute: 'tw-link' })
  twLink: string;

  @property({ type: String, attribute: 'li-link' })
  liLink: string;

  @property({ type: String, attribute: 'ig-link' })
  igLink: string;

  @property({ type: String, attribute: 'yt-link' })
  ytLink: string;

  @property({ type: String, attribute: 'pst-link' })
  pstLink: string;

  @property({ type: String, attribute: 'pod-link' })
  podCastLink: string;

  @property({ type: String, attribute: 'web-link' })
  webLink: string;

  @property({ type: String, attribute: 'facebook-account' })
  facebookAccount: string;

  @property({ type: String, attribute: 'twitter-account' })
  twitterAccount: string;

  @property({ type: String, attribute: 'linkedin-account' })
  linkedInAccount: string;

  @property({ type: String, attribute: 'youtube-account' })
  youtubeAccount: string;

  @property({ type: String, attribute: 'pinterest-account' })
  pinterestAccount: string;

  @property({ type: String, attribute: 'podcasts-account' })
  podcastsAccount: string;

  @property({ type: String, attribute: 'instagram-account' })
  instagramAccount: string;

  @property({ type: String, attribute: 'web-name' })
  webName: string;

  @property({ type: String, attribute: 'share-page-url' })
  shareUrl: string;

  @property({ type: String, attribute: 'screen-reader-text' })
  ariaLabelText: string;

  @property({ type: String, attribute: 'header-text' })
  headerText: string;

  @property({ type: String, attribute: 'link-size' })
  linkSize = '2rem';

  @property({ type: Boolean, attribute: 'has-separator' })
  hasSeparator: boolean;

  @property({ type: Boolean, attribute: 'white-icons' })
  whiteIcons: boolean;

  @property({ type: String, attribute: 'increased-gap' })
  increasedGap: string;

  /**
   * arial label text to (opens in a new tab)
   */

  @property({ type: String, attribute: 'target-aria-label' })
  targetAriaLabel: string;

  @property({ type: String, attribute: 'mail-to' })
  mailTo: string;

  @property({
    type: String,
    reflect: true,
    attribute: 'content-align',
  })
  contentAlign: HorizontalAlignment;

  @state() hasText: boolean;

  connectedCallback() {
    super.connectedCallback();
    this.hasText = this.ngSlot.exist('text');
  }

  renderIcon(
    url: string,
    name: string,
    account?: string | null,
    library = 'system'
  ) {
    account = account || null;
    if (url) {
      if (name === 'globe') {
        return html`<li>
          <a
            href="${url}"
            target="_blank"
            aria-label="${this.ariaLabelText} ${account} ${this
              .targetAriaLabel}"
            class="icon-container ${this.footerNoTracker
              ? ''
              : 'social-share-icons'}"
          >
            <tufts-icon
              library=${library}
              name=${name}
              size=${this.linkSize}
            ></tufts-icon>
            ${account ? html`<span class="icon-text">${account}</span>` : null}
            ${account ? this.indicatorIconTemplate() : null}
          </a>
        </li>`;
      }
      const social = name.replace(/^fa[rbs]-/, '');
      return html`<li>
        <a
          href="${url}"
          target="_blank"
          aria-label="${this.ariaLabelText} ${social === 'envelope'
            ? 'email'
            : social} ${this.targetAriaLabel}"
          class="icon-container ${this.footerNoTracker
            ? ''
            : 'social-share-icons'}"
        >
          <tufts-icon
            library=${library}
            name=${name}
            size=${this.linkSize}
          ></tufts-icon>
          ${account ? html`<span class="icon-text">${account}</span>` : null}
          ${account ? this.indicatorIconTemplate() : null}
        </a>
      </li>`;
    } else {
      return;
    }
  }

  render(): TemplateResult {
    const containerClasses = {
      'social-links-container': true,
      'has-separator': this.hasSeparator,
      'white': this.whiteIcons,
    };

    const iconClasses = {
      'icons-container': true,
      'white': this.whiteIcons,
    };

    return html`
      ${this.headerTemplate()}
      <div class="${classMap(containerClasses)}">
        ${this.textTemplate()}
        <div class="${classMap(iconClasses)}">
          <ul class="${this.increasedGap}">
            ${this.shareUrl
              ? html`${this.renderIcon(
                  'https://www.facebook.com/sharer/sharer.php?u=' +
                    this.shareUrl,
                  'fab-facebook'
                )}
                ${this.renderIcon(
                  'https://x.com/intent/tweet?url=' + this.shareUrl,
                  'fab-twitter'
                )}
                ${this.renderIcon(
                  'https://www.linkedin.com/shareArticle?mini=true&url=' +
                    this.shareUrl,
                  'fab-linkedin'
                )}
                ${this.mailTo
                  ? this.renderIcon('mailto:' + this.mailTo, 'fas-envelope')
                  : null}`
              : html`
                  ${this.renderIcon(
                    this.fbLink,
                    'fab-facebook',
                    this.facebookAccount
                  )}
                  ${this.renderIcon(
                    this.twLink,
                    'fab-twitter',
                    this.twitterAccount
                  )}
                  ${this.renderIcon(
                    this.liLink,
                    'fab-linkedin',
                    this.linkedInAccount
                  )}
                  ${this.renderIcon(
                    this.igLink,
                    'fab-instagram',
                    this.instagramAccount
                  )}
                  ${this.renderIcon(
                    this.ytLink,
                    'fab-youtube',
                    this.youtubeAccount
                  )}
                  ${this.renderIcon(
                    this.pstLink,
                    'fab-pinterest',
                    this.pinterestAccount
                  )}
                  ${this.renderIcon(
                    this.podCastLink,
                    'fab-podcast',
                    this.podcastsAccount
                  )}
                  ${this.renderIcon(this.webLink, 'globe', this.webName)}
                `}
          </ul>
        </div>
      </div>
    `;
  }

  /**
   * Template partial for the indicator Icon.
   * @returns TemplateResult | null
   */
  indicatorIconTemplate(): TemplateResult | null {
    return html` <span class="indicador-icon">
      <tufts-icon library="system" name="link-out" size="1rem"></tufts-icon>
    </span>`;
  }

  /**
   * Template partial for the text slot.
   * @returns TemplateResult | null
   */
  textTemplate(): TemplateResult | null {
    if (!this.hasText) return null;

    const separatorClasses = {
      separator: true,
      white: this.whiteIcons,
    };

    return html`
      <div class="text-container">
        <slot name="text"></slot>
        ${this.hasSeparator
          ? html`<hr class="${classMap(separatorClasses)}" />`
          : null}
      </div>
    `;
  }

  headerTemplate(): TemplateResult | null {
    if (!this.headerText) return null;

    return html` <div class="header-container">${this.headerText}</div> `;
  }
}
